import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PanchTatvaButton from '../components/PanchTatvaButton';

const SUPPORT_EMAIL = 'support@panch-tatva.co.in';
const SUPPORT_PHONE = '+91 6390725752';
const INSTAGRAM_URL = 'https://www.instagram.com/panch.tatva/';

export default function ContactUs() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Build mailto link as a simple no-backend contact method
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`
    );
    const subject = encodeURIComponent(form.subject || 'Customer Query — Panch Tatva');
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  return (
    <div className="w-full max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-20">
      {/* Header */}
      <div className="text-center mb-10 md:mb-14">
        <p className="text-secondary font-medium tracking-widest text-xs md:text-sm uppercase mb-3">Support</p>
        <h1 className="text-3xl md:text-5xl font-heading text-headingText mb-4">Contact Us</h1>
        <p className="text-sm md:text-base text-bodyText opacity-80 max-w-xl mx-auto">
          We'd love to hear from you. Send us a message and we'll respond within 24 hours on business days.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-12">

        {/* Contact Info */}
        <div className="md:col-span-2 space-y-6">

          <div className="bg-cardBg rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-heading text-headingText mb-4">Get in Touch</h2>
            <div className="space-y-4">

              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="flex items-start gap-3 group"
              >
                <span className="text-2xl">✉️</span>
                <div>
                  <p className="text-xs text-bodyText opacity-60 uppercase tracking-wide mb-0.5">Email</p>
                  <p className="text-sm md:text-base text-secondary group-hover:underline break-all">
                    {SUPPORT_EMAIL}
                  </p>
                </div>
              </a>

              <a
                href={`tel:${SUPPORT_PHONE.replace(/\s/g, '')}`}
                className="flex items-start gap-3 group"
              >
                <span className="text-2xl">📞</span>
                <div>
                  <p className="text-xs text-bodyText opacity-60 uppercase tracking-wide mb-0.5">Phone</p>
                  <p className="text-sm md:text-base text-secondary group-hover:underline">
                    {SUPPORT_PHONE}
                  </p>
                </div>
              </a>

              <div className="flex items-start gap-3">
                <span className="text-2xl">🕐</span>
                <div>
                  <p className="text-xs text-bodyText opacity-60 uppercase tracking-wide mb-0.5">Support Hours</p>
                  <p className="text-sm md:text-base text-bodyText">Mon–Sat, 10 AM – 6 PM IST</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="text-2xl">📍</span>
                <div>
                  <p className="text-xs text-bodyText opacity-60 uppercase tracking-wide mb-0.5">Location</p>
                  <p className="text-sm md:text-base text-bodyText">Noida, Uttar Pradesh, India</p>
                </div>
              </div>

            </div>
          </div>

          {/* Social Media */}
          <div className="bg-cardBg rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-heading text-headingText mb-4">Follow Us</h2>
            <div className="flex gap-3">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-sm font-medium hover:opacity-90 transition"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                Instagram
              </a>
            </div>
            <p className="text-xs text-bodyText opacity-60 mt-3">
              Tag us <span className="text-secondary">@panch.tatva</span> — we love seeing Panch Tatva in your rituals.
            </p>
          </div>

          {/* Quick Links */}
          <div className="bg-cardBg rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-heading text-headingText mb-4">Quick Links</h2>
            <ul className="space-y-2 text-sm">
              <li>
                <button onClick={() => navigate('/faq')} className="text-secondary hover:underline">
                  → Frequently Asked Questions
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/returns')} className="text-secondary hover:underline">
                  → Return & Refund Policy
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/orders')} className="text-secondary hover:underline">
                  → Track Your Order
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/help')} className="text-secondary hover:underline">
                  → Help & Support
                </button>
              </li>
            </ul>
          </div>

        </div>

        {/* Contact Form */}
        <div className="md:col-span-3">
          <div className="bg-cardBg rounded-2xl p-6 md:p-8 shadow-sm">
            <h2 className="text-lg font-heading text-headingText mb-6">Send a Message</h2>

            {submitted ? (
              <div className="text-center py-12">
                <div className="text-5xl mb-4">💌</div>
                <h3 className="text-xl font-heading text-headingText mb-2">Message Sent!</h3>
                <p className="text-sm text-bodyText mb-6">
                  Your email app should have opened. We'll reply within 24 hours on business days.
                </p>
                <PanchTatvaButton onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', message: '' }); }} variant="outline">
                  Send Another
                </PanchTatvaButton>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-medium text-bodyText uppercase tracking-wide mb-1.5">
                      Your Name <span className="text-secondary">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="Anukta Sharma"
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-primaryBg text-bodyText focus:outline-none focus:border-secondary transition text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-bodyText uppercase tracking-wide mb-1.5">
                      Email Address <span className="text-secondary">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="you@example.com"
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-primaryBg text-bodyText focus:outline-none focus:border-secondary transition text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-bodyText uppercase tracking-wide mb-1.5">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    placeholder="Order issue, product query, feedback…"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-primaryBg text-bodyText focus:outline-none focus:border-secondary transition text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-bodyText uppercase tracking-wide mb-1.5">
                    Message <span className="text-secondary">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder="Tell us how we can help…"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-primaryBg text-bodyText focus:outline-none focus:border-secondary transition text-sm resize-none"
                  />
                </div>

                <PanchTatvaButton type="submit" fullWidth>
                  Send Message ✉️
                </PanchTatvaButton>

                <p className="text-xs text-bodyText opacity-50 text-center">
                  This will open your email app. Alternatively, email us directly at{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-secondary hover:underline">{SUPPORT_EMAIL}</a>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      <div className="mt-10 text-center">
        <PanchTatvaButton onClick={() => navigate(-1)} variant="outline">
          ← Go Back
        </PanchTatvaButton>
      </div>
    </div>
  );
}
